/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import {
  DELETE_SECTION,
  CREATE_SECTION,
  UPDATE_SECTION,
  UPLOAD_FILE,
} from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";

const log: any = LoggerModule?.getInstance();
const is = require("is_js");

export interface UpdateSectionProps {
  formData: {
    name?: string;
    alias?: string;
    description?: string;
    img1?: string;
    img2?: string;
    images?: any;
    isVisible?: boolean;
    regMode?: string;
    position?: string;
    maxInvites?: number;
    link?: string;
    costumeTypes?: string[];
  };
  id: string;
}

export interface CreateSectionProps {
  formData: {
    alias?: string;
    description?: string;
    img1?: string;
    img2?: string;
    images?: any;
    isVisible?: boolean;
    regMode?: string;
    position?: string;
  };
  id: string;
}
export interface DeleteSectionProps {
  id: string;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ISectionMethods extends IPluginMethodMap {
  updateSection(args: any): Promise<any>;
  createSection(args: any): Promise<any>;
  deleteSection(args: any): Promise<any>;
}

export class SectionService implements IAgentPlugin {
  readonly methods: ISectionMethods;

  constructor() {
    this.methods = {
      updateSection: this.updateSection.bind(this),
      createSection: this.createSection.bind(this),
      deleteSection: this.deleteSection.bind(this),
    };
  }

  async uploadFile(arr: any, formData: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let imagesUrlArray: any = [];
    let fileType = "jpeg";
    if (arr && is.not.empty(arr)) {
      for (let obj of arr) {
        if (obj && is.existy(obj)) {
          const dataString = typeof obj === "string" ? obj : obj?.base64 ?? "";
          if (!dataString) continue;
          if (this.isValidHttpUrl(dataString)) {
            imagesUrlArray.push(dataString);
            continue;
          }
          fileType = this.getFileTypeFromBase64(dataString) ?? "jpeg";
          try {
            if (client) {
              const res = await client.mutate({
                mutation: UPLOAD_FILE,
                variables: {
                  name: fileType,
                  data: dataString,
                  category1: "section",
                  category2: formData?.name ?? "sectionImg",
                  addBandName: true,
                },
                fetchPolicy: "no-cache",
              });
              if (res?.data?.uploadFile) {
                imagesUrlArray.push(res?.data?.uploadFile);
              }
            }
          } catch (err: any) {
            let _display_message =
              err?.graphQLErrors &&
              Array.isArray(err?.graphQLErrors) &&
              err?.graphQLErrors.find((item: any) => item.extensions);
            if (_display_message?.extensions?.display_message) {
              throw Error(_display_message?.extensions?.display_message);
            }

            throw err;
          }
        }
      }
    }

    return imagesUrlArray;
  }

  async createSection(args: CreateSectionProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const imagesArr: [] = await this.uploadFile(
          args?.formData?.images,
          args?.formData,
        );

        if (
          is.existy(args?.formData?.link) &&
          is.not.empty(args?.formData?.link)
        ) {
          imagesArr.push(args?.formData?.link);
        }
        const res = await client.mutate({
          mutation: CREATE_SECTION,
          variables: {
            data: {
              presentation: args?.id,
              images: imagesArr,
              alias: args?.formData?.alias,
              description: args?.formData?.description,
              img1: args?.formData?.img1,
              img2: args?.formData?.img2,
              isVisible: args?.formData?.isVisible,
              name: args?.formData?.name?.trim(),
              maxInvites: args?.formData?.maxInvites,
              regMode: args?.formData?.regMode,
              position: args?.formData?.position,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.createSection,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async updateSection(args: UpdateSectionProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        log.blue(`inviteUser server component222:`, args);
        const imagesArr: [] = await this.uploadFile(
          args?.formData?.images,
          args?.formData,
        );

        if (
          is.existy(args?.formData?.link) &&
          is.not.empty(args?.formData?.link)
        ) {
          imagesArr.push(args?.formData?.link);
        }

        const res = await client.mutate({
          mutation: UPDATE_SECTION,
          variables: {
            filter: {
              _id: args?.id,
            },
            update: {
              // alias: args?.formData?.alias,
              // description: args?.formData?.description,
              // img1: args?.formData?.img1,
              // img2: args?.formData?.img2,
              // isVisible: args?.formData?.isVisible,
              // name: args?.formData?.name?.trim(),
              // maxInvites: args?.formData?.maxInvites,
              // regMode: args?.formData?.regMode,
              // position: args?.formData?.position,
              // images: imagesArr,
              ...(args?.formData?.alias !== undefined && {
                alias: args?.formData?.alias,
              }),
              ...(args?.formData?.description !== undefined && {
                description: args?.formData?.description,
              }),
              ...(args?.formData?.img1 !== undefined && {
                img1: args?.formData?.img1,
              }),
              ...(args?.formData?.img2 !== undefined && {
                img2: args?.formData?.img2,
              }),
              ...(args?.formData?.isVisible !== undefined && {
                isVisible: args?.formData?.isVisible,
              }),
              ...(args?.formData?.name !== undefined && {
                name: args?.formData?.name?.trim(),
              }),
              ...(args?.formData?.maxInvites !== undefined && {
                maxInvites: args?.formData?.maxInvites,
              }),
              ...(args?.formData?.regMode !== undefined && {
                regMode: args?.formData?.regMode,
              }),
              ...(args?.formData?.position !== undefined && {
                position: args?.formData?.position,
              }),
              ...(args?.formData?.costumeTypes !== undefined && {
                costumeTypes: args?.formData?.costumeTypes,
              }),
              ...(args?.formData?.images !== undefined && {
                images: imagesArr,
              }),
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.updateSection,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
  async deleteSection(args: DeleteSectionProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: DELETE_SECTION,
          variables: {
            filter: {
              _id: args?.id,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private isValidHttpUrl(str: string) {
    let url;
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    if (urlRegex.test(str)) {
      return true;
    }
    return false;
  }

  private getFileTypeFromBase64(base64String: string) {
    const matches = base64String.match(/^data:image\/(\w+)/);
    if (matches && matches.length === 2) {
      return matches[1];
    }
    return null; // Invalid base64 string or unable to determine the file type
  }
}
